import React, { useState } from 'react';
import { t } from '@citrite/translate';
import {
	AlertErrorIcon,
	Button,
	CircleWarningIcon,
	layout,
	LoadingPrimary,
	palette,
	typography,
} from '@citrite/web-ui-component';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { LaunchDetails } from 'App/ProgressAlert/LaunchDetails';
import { StyledLoadingDots } from 'App/ProgressAlert/ResourceAlert';
import { getLaunchProgressStatus } from 'App/ProgressAlert/utils';
import { ResourceIcon } from 'Components/ResourceIcon';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { LaunchProgressDetails } from 'Workspace/ResourceProvider/Context';
const imageUrl = require('images/launch-progress-bg.png');

const slideUpAnimation = keyframes`
	from { transform: translateY(100%); }
	to { transform: translateY(0px); }
`;

const slideDownAnimation = keyframes`
	from { transform: translateY(0px); }
	to { transform: translateY(100%); }
`;

const SheetContainer = styled.div<{ isClosing?: boolean }>`
	display: flex;
	flex-direction: column;
	padding: 0;
	width: 100vw;
	border-radius: ${layout.mediumLargeSpace} ${layout.mediumLargeSpace} 0 0;
	margin-top: -${layout.smallSpace};
	align-items: center;
	padding-bottom: ${layout.mediumSpace};
	position: absolute;
	bottom: 0;
	background: ${palette.white};
	animation: ${props => (props.isClosing ? slideDownAnimation : slideUpAnimation)} 0.3s
		ease-in-out;
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0px 0px ${layout.space12};
	gap: ${layout.space12};
	width: 100%;
`;

const TopBackground = styled.div`
	width: 100%;
	height: 156px;
	background-image: url(${imageUrl});
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	border-radius: ${layout.mediumLargeSpace} ${layout.mediumLargeSpace} 0px 0px;
	position: relative;
	overflow: hidden;
`;

const IconContainer = styled.div`
	width: ${layout.space80};
	height: ${layout.space80};
	background: ${palette.white};
	border-radius: ${layout.mediumSpace};
	filter: drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.24));
	display: flex;
	justify-content: center;
	align-items: center;
	margin: -50px auto 0;
	position: relative;
	z-index: 1;
`;

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 0px;
	gap: 6px;
	width: 100%;
	margin-top: 10px;
`;

const Title = styled.h3`
	width: 100%;
	font-weight: ${typography.weights.medium};
	font-size: ${layout.mediumSpace};
	text-align: center;
	margin: 0;
`;

const StatusContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: ${layout.space4};
	gap: ${layout.space8};
`;

const StyledStatusText = styled.span<{ color?: string }>`
	color: ${props => props.color};
	font-size: ${typography.sizes.xsml};
	line-height: 16px;
	font-weight: ${typography.weights.regular};
	white-space: nowrap;
`;

const StyledButton = styled(Button.SubtlePrimary)`
	padding: ${layout.space8} ${layout.space16};
`;

export interface MobileLoaderSheetProps {
	onClose?: (operationId: string) => void;
	onCancel: (operationId: string) => void;
	launchProgressContext: LaunchProgressDetails;
	operationId: string;
}

export const MobileLoaderSheet: React.FC<MobileLoaderSheetProps> = ({
	onClose,
	onCancel,
	launchProgressContext,
	operationId,
}) => {
	const [isClosing, setIsClosing] = useState(false);
	const { value } = useLoadableResourceContext();
	const { resources } = value;
	const { resourceId, error, troubleshoot } = launchProgressContext;
	const resource = resources?.find(res => res.id === resourceId);
	const launchStatus = getLaunchProgressStatus(launchProgressContext);
	const handleClose = () => {
		setIsClosing(true);
	};

	const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget && isClosing && onClose) {
			onClose(operationId);
		}
	};

	const handleButtonClick = () => {
		if (launchStatus === LaunchStatus.Failed) {
			handleClose();
		} else {
			onCancel(operationId);
		}
	};

	return (
		<SheetContainer
			data-testid="mobile-loader-sheet"
			isClosing={isClosing}
			onAnimationEnd={handleAnimationEnd}
		>
			<Header>
				<TopBackground />
				<IconContainer>
					<ResourceIcon resource={resource} size={48} />
				</IconContainer>
				<ContentContainer>
					<Title>{resource?.name}</Title>
					<StatusContainer>
						{launchStatus === LaunchStatus.Failed ? (
							<>
								{troubleshoot ? (
									<>
										<CircleWarningIcon size={15} color={palette.yellow800} />
										<StyledStatusText color={palette.yellow800}>
											{t('Workspace:launch_status.issue_found')}
										</StyledStatusText>
									</>
								) : (
									<>
										<AlertErrorIcon size={15} color={palette.red800} />
										<StyledStatusText color={palette.red800}>
											{t('Workspace:launch_status.failed')}
										</StyledStatusText>
									</>
								)}
							</>
						) : (
							<>
								<LoadingPrimary size={15} />
								<StyledStatusText>
									{t(`Workspace:launch_status.${launchStatus}`)}
									<StyledLoadingDots />
								</StyledStatusText>
							</>
						)}
					</StatusContainer>
					{launchStatus === LaunchStatus.Failed && (
						<LaunchDetails
							transactionId={operationId}
							resource={resource}
							errorMessage={error}
							isTroubleshoot={troubleshoot}
							onClose={handleClose}
						/>
					)}
				</ContentContainer>
			</Header>
			<StyledButton disabled={isClosing} onClick={handleButtonClick}>
				{t('Workspace:close')}
			</StyledButton>
		</SheetContainer>
	);
};
